import { create } from 'zustand'

/**
 * Per-tab unread event counter — driven by realtime pushes and cleared when
 * the user opens the corresponding tab. Persists last-seen server_sequence
 * per tab in localStorage so counts survive reload.
 */

const LS_KEY = 'babyos.unread.lastSeenSeq'

export type TabKey = 'today' | 'timeline' | 'needs' | 'family'

interface UnreadStore {
  counts: Record<TabKey, number>
  bump: (tab: TabKey) => void
  markSeen: (tab: TabKey) => void
  reset: () => void
}

function readLastSeen(): Record<TabKey, number> {
  if (typeof localStorage === 'undefined') return { today: 0, timeline: 0, needs: 0, family: 0 }
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (!raw) return { today: 0, timeline: 0, needs: 0, family: 0 }
    const parsed = JSON.parse(raw)
    return { today: 0, timeline: 0, needs: 0, family: 0, ...parsed }
  } catch { return { today: 0, timeline: 0, needs: 0, family: 0 } }
}

function writeLastSeen(counts: Record<TabKey, number>): void {
  if (typeof localStorage === 'undefined') return
  try { localStorage.setItem(LS_KEY, JSON.stringify(counts)) } catch { /* quota */ }
}

export const useUnread = create<UnreadStore>((set, get) => ({
  counts: readLastSeen(),
  bump: (tab) => {
    const next = { ...get().counts, [tab]: get().counts[tab] + 1 }
    set({ counts: next })
    writeLastSeen(next)
  },
  markSeen: (tab) => {
    if (get().counts[tab] === 0) return
    const next = { ...get().counts, [tab]: 0 }
    set({ counts: next })
    writeLastSeen(next)
  },
  reset: () => {
    const zero: Record<TabKey, number> = { today: 0, timeline: 0, needs: 0, family: 0 }
    set({ counts: zero })
    writeLastSeen(zero)
  },
}))

/** Given an event type, return which tabs should get bumped. */
export function tabsForEvent(eventType: string): TabKey[] {
  if (eventType.startsWith('early_care.') || eventType.startsWith('growth.') || eventType === 'note.created') {
    return ['today', 'timeline']
  }
  if (eventType.startsWith('need.')) return ['needs']
  if (eventType.startsWith('person.') || eventType.startsWith('family.')) return ['family']
  return ['timeline']
}
